/**
 * L'enregistreur de la candidature, ce que ses deux moteurs partagent : la forme du crochet
 * (`useApplicationRecorder`, en `.tsx` pour le téléphone, en `.web.tsx` pour le navigateur) et la
 * phase qu'il dit, en pur.
 */
import type React from "react"

import { type UITutorRecorderPhase } from "../types/tutorCandidature"

export type ApplicationRecorderArgs = {
    /** Le temps de réponse qui reste : à la fin, l'enregistrement s'arrête seul. */
    secondsLeft: number
    /**
     * Le viseur est posé dans une case (à côté de la copie d'une question technique) au lieu du plein
     * écran : il la remplit. Défaut `false`.
     */
    framed?: boolean
}

export type ApplicationRecorder = {
    phase: UITutorRecorderPhase
    /** La caméra frontale, posée dans la page. */
    viewfinder: React.ReactNode
    /** Accès refusé : redemander la caméra et le micro. */
    allow: () => void
    /** La vidéo de CET enregistrement est posée (`state.camera.videoRecord`) : on revient à la question. */
    recorded: boolean
}

/** La phase de l'enregistrement, des autorisations à la caméra qui tourne. */
export function recorderPhase(args: { granted: boolean | null; recording: boolean }): UITutorRecorderPhase {
    if (args.granted == null) return "starting"
    if (!args.granted) return "denied"
    return args.recording ? "recording" : "ready"
}

/**
 * Où se pose le viseur du téléphone. Plein écran, il prend la largeur de la fenêtre et la marge qui
 * centre l'image 4:3 d'Android (`imagePadding`, la hauteur de la fenêtre moins celle de l'image). Dans
 * une case, il la remplit : ces mesures de fenêtre l'en sortaient (une marge haute de 161 px dans la
 * vignette de 144 d'un téléphone, une marge négative dans la colonne d'une tablette en paysage), et le
 * candidat ne se voyait pas, alors que la vidéo, elle, s'enregistrait.
 */
export function nativeViewfinderStyle(args: { framed: boolean; windowWidth: number; imagePadding: number }) {
    if (args.framed) return { flex: 1 }
    return {
        flex: 1,
        alignSelf: "center" as const,
        width: args.windowWidth,
        marginTop: args.imagePadding / 2,
        marginBottom: args.imagePadding / 2,
    }
}

/** Le départ de l'enregistrement après l'ouverture de la caméra : 4 s au téléphone, 2 s au web. */
export const RECORDING_START_DELAY_MS = { native: 4000, web: 2000 }
